<template>
  <div
    class="closing-card"
    :class="{
      '-loading': loading,
      '-small': small,
    }"
    :style="{ left, top, width, height }">
    <div class="earmark"></div>
    <h1 class="title f5">{{ $t('Temporary closure') }}</h1>
    <h2 class="time f8">{{ fromToTime }}</h2>
     <!-- Disabled for the time being per request -->
    <!--<h2 class="time f8">{{ minimumBookingWindow }}</h2>-->
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import OverviewHelper from '@/location/overview/OverviewHelper';
import moment from 'moment-timezone';
import Loading from '@/core/shared/components/Loading';
import DateHelper from '@/core/shared/helpers/DateHelper';

@Component({
  props: {
    closing: {
      type: Object,
      required: true,
    },
    stationId: {
      type: String,
      required: true,
    },
    vertical: {
      type: Boolean,
      default: false,
    },
    twentyFourHourTime: {
      type: Boolean,
      required: true,
    },
    calendarDate: {
      type: String,
      required: true,
    },
    isNetworkAvailable: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Loading,
  },
})
export default class ClosingCard extends Vue {
  get loading () {
    return this.closing.id.includes('loading');
  }

  get leftPercent () {
    return OverviewHelper.timeToPercentThroughDay(moment(this.closing.startTime), this.calendarDate);
  }

  get rightPercent () {
    if (
      DateHelper.dateMatch(this.closing.startTime, this.closing.endTime) ||
      DateHelper.dateMatch(this.closing.endTime, this.calendarDate)
    ) {
      return OverviewHelper.timeToPercentThroughDay(moment(this.closing.endTime));
    } else {
      return 1.001;
    }
  }

  get top () {
    return (this.vertical)
      ? `${100 * this.leftPercent}%`
      : '1rem';
  }

  get left () {
    return (this.vertical)
      ? '0'
      : `${100 * this.leftPercent}%`;
  }

  get width () {
    if (this.vertical) {
      return 'calc(100% - 10rem)';
    } else {
      if (this.rightPercent < this.leftPercent) return '10000px';
      return `${100 * (this.rightPercent - this.leftPercent)}%`;
    }
  }

  get height () {
    return (this.vertical)
      ? `${100 * (this.rightPercent - this.leftPercent)}%`
      : '11rem';
  }

  get small () {
    return parseInt(this.width) < 99;
  }

  get fromToTime () {
    if (this.twentyFourHourTime) {
      return moment(this.closing.startTime).format('H:mm') + ' - ' + moment(this.closing.endTime).format('H:mm');
    } else {
      return moment(this.closing.startTime).format('h:mm a') + ' - ' + moment(this.closing.endTime).format('h:mm a');
    }
  }

  get minimumBookingWindow () {
    const selection = new Date();
    selection.setHours(Math.floor(this.closing.minimumBookingWindow / 60), this.closing.minimumBookingWindow % 60);
    return selection.getHours() + 'H' + selection.getMinutes().toString().padStart(2, '0') + ' MBW';
  }

  get ended () {
    return moment(this.closing.startTime).diff(moment(this.currentTime)) < 0 && this.closing.startTime;
  }
}
</script>

<style scoped lang="postcss">
@import 'core/shared/styles';

.closing-card {
  position: absolute;
  background: repeating-linear-gradient(
    135deg,
    pink 0px,
    pink 2px,
    white 2px,
    white 5px
  );
  border-radius: var(--radius);
  cursor: pointer;
  overflow: hidden;
  transition: background-color 0.3s ease;
  padding: 0 var(--spacingSm);
  padding-top: 1.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadowSm);
  z-index: 1;

  & .title {
    font-size: 1.4rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin: 0;
    line-height: 1;
    margin-bottom: var(--spacingXS);
  }

  & .time {
    color: var(--colorManatee);
    line-height: 1.2;
    margin: 0;
    margin-bottom: var(--spacingXS);
  }

  & .earmark {
    position: absolute;
    top: -1rem;
    left: -0.2rem;
    width: 0;
    height: 0;
    border-top: 1.5rem solid transparent;
    border-bottom: 1.5rem solid transparent;
    border-right: 1.5rem solid var(--colorTransparent);
    opacity: 0;
    transform: rotate(45deg);
    border-radius: 0.4rem;
    transition: opacity 0.3s ease;
  }

  &.-loading {
    padding-top: 0;
    opacity: 0.9;

    & > * {
      display: none;
    }

    & .loadinganimation {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
  }

  &.-small {
    padding-left: var(--spacingXS);
    padding-right: var(--spacingXS);

    & .time, & .notpaid, & .playingstatus, & .-conflictstatus {
      font-size: 0.7rem;
    }
  }
}
</style>
